import { useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Plus, X } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Button, Input, Select, toast } from '@/components/ui';
import type { ProductFormValues } from '../schemas/productSchema';
import {
  NOTICE_SPEC_TEMPLATES,
  applyNoticeSpecTemplate,
  findNoticeSpecTemplate,
} from '../lib/noticeSpecTemplates';
import { useNoticeSpecTemplatesQuery } from '../noticeSpecTemplates/hooks/useNoticeSpecTemplates';

/**
 * noticeSpec — 상품 정보 고시 key-value 행 editor.
 * 행 단위 {key, value} 입력. 추가 / 삭제.
 * 입력 시 Record<string, string> 으로 reconstruct.
 *
 * 15-A-2 (ID80) — 품목별 표준 고시항목 템플릿 적용. 셀렉터에서 품목을 선택하고
 * [템플릿 적용] 하면 법정 필수 표시항목 키가 빈 값으로 시드된다(기존 입력 보존).
 */
// [notice-tpl] §11 적용이 함께 시드하는 번역 locale(ko 는 본 편집기 정본).
const TEMPLATE_TRANSLATION_LOCALES = ['en-US', 'ja-JP', 'zh-CN'] as const;
type TemplateTranslationLocale = (typeof TEMPLATE_TRANSLATION_LOCALES)[number];
const LOCALE_SHORT_LABELS: Record<TemplateTranslationLocale, string> = {
  'en-US': '영문',
  'ja-JP': '일문',
  'zh-CN': '중문',
};

export const NoticeSpecEditor = () => {
  const { control, getValues, setValue } = useFormContext<ProductFormValues>();
  const { t } = useTranslation('products');

  // D8 — §1 기본정보에 이미 입력된 원산지/제조연월/제조사를 템플릿의 동일 의미 키에 시드해
  // 재입력(이중 수집)을 없앤다. 운영자가 이후 자유롭게 수정 가능(빈 값만 채움, 덮어쓰기 없음).
  const buildBasicInfoSeeds = (): Record<string, string> => {
    const seeds: Record<string, string> = {};
    const origin = getValues('originCountry')?.trim();
    const yearMonth = getValues('manufacturedYearMonth')?.trim();
    const maker = getValues('manufacturerName')?.trim();
    if (origin) seeds['제조국'] = origin;
    if (yearMonth) seeds['제조연월'] = yearMonth;
    if (maker) seeds['제조자(수입자)'] = maker;
    return seeds;
  };
  const [selectedTemplate, setSelectedTemplate] = useState('');
  // QA-80 — BO 에서 관리하는 고시 템플릿(마스터) 선택. 정적 표준 품목(키만)과 별개로, 운영자가
  // 값까지 채워 저장한 템플릿을 그대로 적용한다.
  const [selectedManagedId, setSelectedManagedId] = useState('');
  const managedTemplates = useNoticeSpecTemplatesQuery();

  // QA-80 ① — 값 입력은 onChange 로 즉시 반영하는데 키마다 onChange→field.onChange(next)→
  // Controller 재렌더가 일어난다. CJK(한글/중문/일문) IME 는 조합(composition) 중 이 재렌더가
  // 끼면 조합이 깨져 글자가 입력되지 않는다(영문/숫자/특수문자도 빠른 입력 시 누락 가능).
  // 조합 중에는 onChange 반영을 보류하고 compositionEnd 에서 1회 커밋한다.
  // (템플릿 모달 편집기 NoticeSpecItemsEditor 와 동일한 검증된 패턴.)
  const composingRef = useRef(false);

  return (
    <Controller
      control={control}
      name="noticeSpec"
      render={({ field }) => {
        const value = (field.value ?? {}) as Record<string, string>;
        const entries = useMemo(() => Object.entries(value), [value]);

        const applyTemplate = () => {
          const template = findNoticeSpecTemplate(selectedTemplate);
          if (!template) return;
          const { next, addedCount } = applyNoticeSpecTemplate(value, template, buildBasicInfoSeeds());
          field.onChange(next);
          toast.success(
            addedCount > 0
              ? t('products.fields.noticeSpecTemplateApplied', '{{label}} 템플릿 {{count}}개 항목을 추가했습니다.')
                  .replace('{{label}}', template.label)
                  .replace('{{count}}', String(addedCount))
              : t('products.fields.noticeSpecTemplateNoChange', '{{label}} 템플릿 항목이 이미 모두 추가되어 있습니다.')
                  .replace('{{label}}', template.label),
          );
        };

        // QA-80 — 저장된(BO 관리) 템플릿 적용: 항목 라벨→값을 noticeSpec 에 병합.
        // 기존에 값이 입력된 키는 보존하고, 템플릿에만 있는 키를 추가한다(값 포함).
        //
        // [notice-tpl 사용자 재정의] 템플릿의 몰별(en/ja/zh) 항목(V106 translations)도 함께
        // 상품의 per-locale 고시(translations.{loc}.noticeSpec, §2 다국어 탭)에 시드한다 —
        // "적용 시 몰별 동반 적용". ★템플릿에 그 locale 번역이 **있을 때만** 시드(없는 locale 에
        // ko 를 복사하면 SF 의 읽기시 ko 폴백 시맨틱이 깨지고 중복 데이터가 영속된다).
        // 병합은 ko 와 동일한 add-only(기존 키 보존 — 운영자 수정 미덮어쓰기).
        const applyManagedTemplate = () => {
          const tpl = (managedTemplates.data ?? []).find((m) => m.templateId === selectedManagedId);
          if (!tpl) return;
          const next: Record<string, string> = { ...value };
          let addedCount = 0;
          for (const [k, v] of Object.entries(tpl.items)) {
            if (!Object.prototype.hasOwnProperty.call(next, k)) {
              next[k] = v;
              addedCount += 1;
            }
          }
          field.onChange(next);

          const seededLocales: string[] = [];
          for (const loc of TEMPLATE_TRANSLATION_LOCALES) {
            const tplItems = tpl.translations?.[loc];
            if (!tplItems || Object.keys(tplItems).length === 0) continue;
            const current = (getValues(`translations.${loc}.noticeSpec`) ?? {}) as Record<string, string>;
            const merged: Record<string, string> = { ...current };
            let localeAdded = 0;
            for (const [k, v] of Object.entries(tplItems)) {
              if (!Object.prototype.hasOwnProperty.call(merged, k)) {
                merged[k] = v;
                localeAdded += 1;
              }
            }
            if (localeAdded > 0) {
              setValue(`translations.${loc}.noticeSpec`, merged, { shouldDirty: true });
              seededLocales.push(LOCALE_SHORT_LABELS[loc]);
            }
          }

          const baseMessage =
            addedCount > 0
              ? t('products.fields.noticeSpecTemplateApplied', '{{label}} 템플릿 {{count}}개 항목을 추가했습니다.')
                  .replace('{{label}}', tpl.name)
                  .replace('{{count}}', String(addedCount))
              : t('products.fields.noticeSpecTemplateNoChange', '{{label}} 템플릿 항목이 이미 모두 추가되어 있습니다.')
                  .replace('{{label}}', tpl.name);
          const localeSuffix =
            seededLocales.length > 0
              ? ' ' +
                t('products.fields.noticeSpecTemplateLocalesSeeded', '{{locales}} 고시 항목도 함께 적용했습니다(§2 다국어 탭).')
                  .replace('{{locales}}', seededLocales.join('·'))
              : '';
          toast.success(baseMessage + localeSuffix);
        };

        const updateEntry = (idx: number, key: string, val: string) => {
          const next: Record<string, string> = {};
          entries.forEach(([k, v], i) => {
            if (i === idx) {
              if (key.trim()) next[key] = val;
            } else if (k.trim()) {
              next[k] = v;
            }
          });
          field.onChange(next);
        };

        const removeEntry = (idx: number) => {
          const next: Record<string, string> = {};
          entries.forEach(([k, v], i) => {
            if (i !== idx && k.trim()) next[k] = v;
          });
          field.onChange(next);
        };

        const addEntry = () => {
          const next: Record<string, string> = { ...value };
          // 빈 키 한 줄 추가. 사용자가 input 채우면 reconstruct.
          let placeholder = '신규 항목';
          let n = 1;
          while (placeholder in next) {
            n += 1;
            placeholder = `신규 항목 ${n}`;
          }
          next[placeholder] = '';
          field.onChange(next);
        };

        return (
          <div className="flex flex-col gap-2">
            {/* 15-A-2 (ID80) 품목별 표준 고시 템플릿 적용 */}
            <div
              className="flex flex-wrap items-center gap-2 rounded-md p-3"
              style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
            >
              <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
                {t('products.fields.noticeSpecTemplateLabel', '고시 템플릿')}
              </span>
              <Select
                aria-label={t('products.fields.noticeSpecTemplateSelect', '품목 고시 템플릿 선택')}
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                style={{ width: 'auto', minWidth: '200px', flex: '0 1 auto' }}
              >
                <option value="">
                  {t('products.fields.noticeSpecTemplatePlaceholder', '품목 선택')}
                </option>
                {NOTICE_SPEC_TEMPLATES.map((tpl) => (
                  <option key={tpl.code} value={tpl.code}>
                    {tpl.label}
                  </option>
                ))}
              </Select>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!selectedTemplate}
                onClick={applyTemplate}
              >
                {t('products.fields.noticeSpecTemplateApply', '템플릿 적용')}
              </Button>
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {t(
                  'products.fields.noticeSpecTemplateHint',
                  '표준 항목 키를 추가합니다. 기존 입력값은 유지됩니다.',
                )}
              </span>
            </div>

            {/* QA-80 — BO 관리 고시 템플릿(값 포함) 선택·적용. 관리 페이지에서 등록한 템플릿 목록. */}
            <div
              className="flex flex-wrap items-center gap-2 rounded-md p-3"
              style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
            >
              <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
                {t('products.fields.noticeSpecManagedLabel', '저장된 템플릿')}
              </span>
              <Select
                aria-label={t('products.fields.noticeSpecManagedSelect', '저장된 고시 템플릿 선택')}
                value={selectedManagedId}
                onChange={(e) => setSelectedManagedId(e.target.value)}
                disabled={managedTemplates.isLoading}
                style={{ width: 'auto', minWidth: '220px', flex: '0 1 auto' }}
              >
                <option value="">
                  {managedTemplates.isLoading
                    ? t('products.fields.noticeSpecManagedLoading', '불러오는 중…')
                    : t('products.fields.noticeSpecManagedPlaceholder', '템플릿 선택')}
                </option>
                {(managedTemplates.data ?? []).map((tpl) => (
                  <option key={tpl.templateId} value={tpl.templateId}>
                    {tpl.name}
                  </option>
                ))}
              </Select>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!selectedManagedId}
                onClick={applyManagedTemplate}
              >
                {t('products.fields.noticeSpecTemplateApply', '템플릿 적용')}
              </Button>
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {t(
                  'products.fields.noticeSpecManagedHint',
                  '상품정보고시 템플릿 관리에서 등록한 템플릿을 값까지 적용합니다.',
                )}
              </span>
            </div>

            {entries.length === 0 ? (
              <div
                className="flex items-center justify-center rounded-md py-6 text-[13px]"
                style={{
                  background: 'var(--surface-0)',
                  border: '1px dashed var(--divider-strong)',
                  color: 'var(--content-muted)',
                }}
              >
                {t('products.fields.noticeSpecEmpty', '등록된 고시 항목이 없습니다.')}
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {entries.map(([key, val], idx) => (
                  <li
                    key={`${idx}-${key}`}
                    className="grid items-center gap-2"
                    // 좁은 컨테이너에서도 값 입력 폭을 확보하기 위해 반응형 트랙 사용(고시 항목 editor 일관).
                    // minmax(0, …) 가 grid item 기본 min-width:auto 를 무력화해 두 입력 모두 shrink 허용.
                    // 풀폭 폼에서는 라벨 칼럼 200px 유지라 외관 무변경.
                    style={{ gridTemplateColumns: 'minmax(120px, 200px) minmax(0, 1fr) auto' }}
                  >
                    <Input
                      type="text"
                      defaultValue={key}
                      placeholder={t('products.fields.noticeSpecKey', '항목명 (예: 원산지)')}
                      onBlur={(e) => updateEntry(idx, e.target.value, val)}
                    />
                    <Input
                      type="text"
                      defaultValue={val}
                      placeholder={t('products.fields.noticeSpecValue', '값 (예: 대한민국)')}
                      onCompositionStart={() => {
                        composingRef.current = true;
                      }}
                      onCompositionEnd={(e) => {
                        composingRef.current = false;
                        updateEntry(idx, key, e.currentTarget.value);
                      }}
                      onChange={(e) => {
                        // IME 조합 중에는 재렌더를 보류(조합 깨짐 방지). 종료 시 compositionEnd 에서 커밋.
                        if (composingRef.current) return;
                        updateEntry(idx, key, e.target.value);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEntry(idx)}
                      aria-label={t('products.fields.noticeSpecRemove', '삭제')}
                    >
                      <X size={14} style={{ color: 'var(--signal-danger)' }} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div>
              <Button type="button" variant="secondary" size="sm" onClick={addEntry}>
                <Plus size={14} />
                {t('products.fields.noticeSpecAdd', '항목 추가')}
              </Button>
            </div>
          </div>
        );
      }}
    />
  );
};
